import { useMemo, useState, useCallback } from 'react';
import { Form, message } from 'antd';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useCanCheck } from '@/ability/helpers';
import { PROJECT_TEMPLATES_MANAGE } from '@/ability';
import {
    getProjectTemplates,
    createProjectTemplate,
    updateProjectTemplate,
    deleteProjectTemplate,
} from '@services/projectTemplates.service';

export const PROJECT_TEMPLATES_QUERY_KEY = ['project-templates'];

export function useProjectTemplates() {
    const queryClient = useQueryClient();
    const can = useCanCheck();
    const canManage = can(PROJECT_TEMPLATES_MANAGE);

    const [form] = Form.useForm();
    const [modalOpen, setModalOpen] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null);

    const query = useQuery({
        queryKey: PROJECT_TEMPLATES_QUERY_KEY,
        queryFn: () => getProjectTemplates(),
    });

    const templates = useMemo(() => {
        const raw = query.data;
        if (Array.isArray(raw)) return raw;
        if (raw && Array.isArray(raw.data)) return raw.data;
        return [];
    }, [query.data]);

    const createMutation = useMutation({
        mutationFn: createProjectTemplate,
        onSuccess: () => {
            message.success('Project template created successfully');
            queryClient.invalidateQueries({ queryKey: PROJECT_TEMPLATES_QUERY_KEY });
            closeModal();
        },
        onError: (err) => {
            message.error(err?.response?.data?.message ?? err?.message ?? 'Failed to create template');
        },
    });

    const updateMutation = useMutation({
        mutationFn: ({ id, body }) => updateProjectTemplate(id, body),
        onSuccess: () => {
            message.success('Project template updated successfully');
            queryClient.invalidateQueries({ queryKey: PROJECT_TEMPLATES_QUERY_KEY });
            closeModal();
        },
        onError: (err) => {
            message.error(err?.response?.data?.message ?? err?.message ?? 'Failed to update template');
        },
    });

    const deleteMutation = useMutation({
        mutationFn: deleteProjectTemplate,
        onSuccess: () => {
            message.success('Project template deleted successfully');
            queryClient.invalidateQueries({ queryKey: PROJECT_TEMPLATES_QUERY_KEY });
        },
        onError: (err) => {
            message.error(err?.response?.data?.message ?? err?.message ?? 'Failed to delete template');
        },
    });

    const openCreateModal = useCallback(() => {
        setEditingTemplate(null);
        form.resetFields();
        form.setFieldsValue({
            code: '',
            name: '',
            description: '',
            usePhases: true,
            useMilestones: true,
            useSprints: true,
            useTasks: true,
            useSubmissionMemo: false,
            submissionMode: 'formal',
            allowedProjectTypes: [],
        });
        setModalOpen(true);
    }, [form]);

    const openEditModal = useCallback((template) => {
        setEditingTemplate(template);
        form.resetFields();
        form.setFieldsValue({
            code: template.code,
            name: template.name,
            description: template.description,
            usePhases: template.usePhases,
            useMilestones: template.useMilestones,
            useSprints: template.useSprints,
            useTasks: template.useTasks,
            useSubmissionMemo: template.useSubmissionMemo,
            submissionMode: template.submissionMode ?? 'formal',
            allowedProjectTypes: template.allowedProjectTypes ?? [],
        });
        setModalOpen(true);
    }, [form]);

    const closeModal = useCallback(() => {
        setModalOpen(false);
        setEditingTemplate(null);
        form.resetFields();
    }, [form]);

    const handleSubmit = useCallback(async () => {
        try {
            const values = await form.validateFields();
            if (editingTemplate) {
                const { code, ...updatePayload } = values;
                updateMutation.mutate({ id: editingTemplate.id, body: updatePayload });
            } else {
                createMutation.mutate(values);
            }
        } catch (e) {
            // validation errors
        }
    }, [form, editingTemplate, createMutation, updateMutation]);

    return {
        templates,
        isLoading: query.isPending,
        isError: query.isError,
        refetch: query.refetch,
        modalOpen,
        editingTemplate,
        form,
        openCreateModal,
        openEditModal,
        closeModal,
        handleSubmit,
        canManage,
        isSaving: createMutation.isPending || updateMutation.isPending,
        deleteTemplate: deleteMutation.mutate,
        isDeleting: deleteMutation.isPending,
    };
}
